import type { Schedule, Task } from "../../shared/types.ts";
import { recallBoard, recallSchedules, rememberBoard, rememberSchedules } from "../board-cache.ts";

/**
 * A write's own answer, written into the boards the app remembers (src/board-cache.ts), so the board
 * a task screen or Add Task lands on already shows the change on its first frame: a posted card is
 * there, a finished one has moved to Done, a deleted one is gone. The board still fetches on mount
 * and the server's list replaces this; nothing here is authoritative, it only spares the peon the
 * second request's wait. A board never remembered is left alone (it loads fresh anyway).
 */
function edit(status: "open" | "done", change: (tasks: Task[]) => Task[]): void {
  const summary = recallBoard(status);
  if (!summary) return;
  rememberBoard(status, { ...summary, tasks: change(summary.tasks) });
}

const without = (id: string) => (tasks: Task[]) => tasks.filter((t) => t.id !== id);

/** A task the server just created or reopened: at the end of To do, where the newest wait sits (urgent ones are lifted by the board). */
export function boardAdded(task: Task): void {
  edit("done", without(task.id));
  edit("open", (tasks) => [...without(task.id)(tasks), task]);
}

/** A task the server just finished: off To do, and first on Done (newest first). */
export function boardFinished(task: Task): void {
  edit("open", without(task.id));
  edit("done", (tasks) => [task, ...without(task.id)(tasks)]);
}

/**
 * A task the server just changed in place (a claim taken or let go, a done task's credit given): the
 * same card on its own board, its new fields.
 */
export function boardChanged(task: Task): void {
  edit(task.status, (tasks) => tasks.map((t) => (t.id === task.id ? task : t)));
}

/** A task the server just deleted: off both boards. */
export function boardRemoved(id: string): void {
  edit("open", without(id));
  edit("done", without(id));
}

/** A schedule the server just created, changed or deleted, written into the remembered Schedules list the same way. */
export function schedulesChanged(change: { saved: Schedule } | { removed: string }): void {
  const list = recallSchedules();
  if (!list) return;
  if ("removed" in change) {
    rememberSchedules(list.filter((s) => s.id !== change.removed));
    return;
  }
  const { saved } = change;
  rememberSchedules(list.some((s) => s.id === saved.id) ? list.map((s) => (s.id === saved.id ? saved : s)) : [...list, saved]);
}
